<template>
  <div
    class="review-stars"
    :role="ariaLabel ? 'img' : null"
    :aria-label="ariaLabel ? ariaLabel : null"
    :aria-hidden="ariaLabel ? null : 'true'"
    :style="containerStyle"
  >
    <svg
      v-for="(fill, i) in fills"
      :key="i"
      viewBox="0 0 16 16"
      aria-hidden="true"
      focusable="false"
      class="review-stars__star"
    >
      <defs v-if="fill > 0 && fill < 1">
        <clipPath :id="`${uid}-${i}`">
          <!-- Map fill to the star's actual visible bounds rather than the
               raw 16-unit viewBox, so a 0.9 fill leaves a visible sliver
               instead of clipping past the narrow right point. -->
          <rect
            x="0"
            y="0"
            :width="starLeft + fill * starVisibleWidth"
            height="16"
          />
        </clipPath>
      </defs>
      <path
        v-if="fill < 1"
        class="review-stars__star-empty"
        :d="starPath"
        fill-rule="evenodd"
      />
      <path
        v-if="fill > 0"
        class="review-stars__star-filled"
        :d="starPath"
        fill-rule="evenodd"
        :clip-path="fill < 1 ? `url(#${uid}-${i})` : null"
      />
    </svg>
  </div>
</template>

<script>
let starCounter = 0;
// Star path from /wp-content/assets/icons/icon-star.svg — fuller-bodied star
// with rounded inner corners. Drawn in a 16×16 viewBox.
const STAR_PATH =
  'm10.67 4.902 4.53.5c.768.085 1.078 1.082.504 1.623l-3.383 3.187.945 4.658c.16.79-.651 1.406-1.321 1.003L8 13.494l-3.945 2.379c-.67.403-1.481-.212-1.32-1.003l.944-4.658L.297 7.025C-.278 6.484.032 5.487.8 5.402l4.529-.5L7.184.553a.879.879 0 0 1 1.632 0l1.854 4.349z';
// Horizontal bounds of the star inside the 16×16 viewBox; used to map
// fractional fills to the star's actual visible width.
const STAR_LEFT_X = 0.297;
const STAR_VISIBLE_WIDTH = 15.407;

export default {
  name: 'ReviewStars',

  props: {
    // Integer (0-5). Backwards-compatible with original `count` API.
    count: {
      type: Number,
      default: null,
    },
    // Decimal rating (0-5). Takes precedence over `count` when provided.
    rating: {
      type: [Number, String],
      default: null,
    },
    // Star side length in px. When null, falls back to CSS (`1em` by default),
    // so the stars scale with the parent's font-size.
    size: {
      type: Number,
      default: null,
    },
    color: {
      type: String,
      default: null,
    },
    emptyColor: {
      type: String,
      default: null,
    },
    spacing: {
      type: Number,
      default: null,
    },
    ariaLabel: {
      type: String,
      default: '',
    },
  },

  data() {
    starCounter += 1;
    return {
      uid: `review-stars-${starCounter}`,
      starPath: STAR_PATH,
      starLeft: STAR_LEFT_X,
      starVisibleWidth: STAR_VISIBLE_WIDTH,
    };
  },

  computed: {
    numericRating() {
      if (this.rating !== null && this.rating !== '') {
        const value = Number.parseFloat(this.rating);
        return Number.isFinite(value) ? value : 0;
      }
      if (this.count !== null && Number.isFinite(this.count)) {
        return this.count;
      }
      return 0;
    },

    fills() {
      const clamped = Math.max(0, Math.min(5, this.numericRating));
      return [1, 2, 3, 4, 5].map((i) =>
        Math.max(0, Math.min(1, clamped - (i - 1))),
      );
    },

    containerStyle() {
      const style = {};
      if (this.size !== null) {
        style['--review-stars-size'] = `${this.size}px`;
      }
      if (this.spacing !== null) {
        style['--review-stars-gap'] = `${this.spacing}px`;
      }
      if (this.color) {
        style['--review-stars-color'] = this.color;
      }
      if (this.emptyColor) {
        style['--review-stars-empty-color'] = this.emptyColor;
      }
      return style;
    },
  },
};
</script>
